import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Grid
} from '@mui/material';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';
import { PayrollRecord, Worker } from '../../../types/worker';

interface PayrollManagementProps {
  farmId: string;
  workers: Worker[];
}

const PayrollManagement: React.FC<PayrollManagementProps> = ({ farmId, workers }) => {
  const [records, setRecords] = useState<PayrollRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [newRecord, setNewRecord] = useState<Partial<PayrollRecord>>({
    status: 'pending',
    deductions: 0,
    bonuses: 0
  });

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('payroll_records')
        .select('*')
        .eq('farm_id', farmId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mappedRecords = (data || []).map((r: any) => ({
        ...r,
        workerId: r.worker_id,
        farmId: r.farm_id,
        periodStart: r.period_start,
        periodEnd: r.period_end,
        baseSalary: r.base_salary,
        totalPay: r.total_pay,
        paymentDate: r.payment_date,
        createdAt: r.created_at
      }));

      setRecords(mappedRecords);
    } catch (err: any) {
      console.error('Error fetching payroll:', err);
      setError('Failed to load payroll records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (farmId) fetchRecords();
  }, [farmId]);

  const handleMarkPaid = async (id: string) => {
    try {
      const { error } = await supabase
        .from('payroll_records')
        .update({ 
          status: 'paid',
          payment_date: new Date().toISOString().split('T')[0]
        })
        .eq('id', id);

      if (error) throw error;
      await fetchRecords();
    } catch (err) {
      console.error('Error marking paid:', err);
      setError('Failed to update status');
    }
  };

  const calculateTotal = (base: number, bonus: number, deduction: number) => {
    return Number(base) + Number(bonus) - Number(deduction);
  };

  const handleWorkerSelect = (workerId: string) => {
    const worker = workers.find(w => w.id === workerId);
    if (worker) {
      setNewRecord(prev => ({
        ...prev,
        workerId,
        baseSalary: worker.wage,
        totalPay: calculateTotal(worker.wage, prev.bonuses || 0, prev.deductions || 0)
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      if (!newRecord.workerId || !newRecord.periodStart || !newRecord.periodEnd || newRecord.baseSalary === undefined) {
        setError('Please fill in all required fields');
        return;
      }

      const totalPay = calculateTotal(newRecord.baseSalary, newRecord.bonuses || 0, newRecord.deductions || 0);

      const { error } = await supabase
        .from('payroll_records')
        .insert([{
          worker_id: newRecord.workerId,
          farm_id: farmId,
          period_start: newRecord.periodStart,
          period_end: newRecord.periodEnd,
          base_salary: newRecord.baseSalary,
          deductions: newRecord.deductions || 0,
          bonuses: newRecord.bonuses || 0,
          total_pay: totalPay,
          status: 'pending',
          notes: newRecord.notes
        }]);

      if (error) throw error;
      
      setOpenDialog(false);
      setNewRecord({ status: 'pending', deductions: 0, bonuses: 0 });
      await fetchRecords();
    } catch (err) {
      console.error('Error creating payroll record:', err);
      setError('Failed to create payroll record');
    }
  };

  const getWorkerName = (id: string) => {
    return workers.find(w => w.id === id)?.name || 'Unknown Worker';
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Payroll Management</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Generate Payment
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Worker</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Base</TableCell>
                  <TableCell align="right">Bonus/Deduct</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No payroll records found</TableCell>
                  </TableRow>
                ) : (
                  records.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>{format(new Date(record.createdAt), 'MMM d, yyyy')}</TableCell>
                      <TableCell>{getWorkerName(record.workerId)}</TableCell>
                      <TableCell>
                        {format(new Date(record.periodStart), 'MMM d')} - {format(new Date(record.periodEnd), 'MMM d')}
                      </TableCell>
                      <TableCell align="right">${record.baseSalary.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="success.main" display="block">+{record.bonuses}</Typography>
                        <Typography variant="caption" color="error.main" display="block">-{record.deductions}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        ${record.totalPay.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={record.status} 
                          size="small" 
                          color={record.status === 'paid' ? 'success' : 'warning'}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {record.status === 'pending' && (
                          <Button 
                            size="small" 
                            startIcon={<ReceiptIcon />}
                            onClick={() => handleMarkPaid(record.id)}
                          >
                            Mark Paid
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Payroll Entry</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Select Worker"
              fullWidth
              value={newRecord.workerId || ''}
              onChange={(e) => handleWorkerSelect(e.target.value)}
              SelectProps={{ native: true }}
            >
              <option value="">Select a worker...</option>
              {workers.map((worker) => (
                <option key={worker.id} value={worker.id}>
                  {worker.name} ({worker.role}) - ${worker.wage}/{worker.paymentType}
                </option>
              ))}
            </TextField>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  type="date"
                  label="Period Start"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={newRecord.periodStart || ''}
                  onChange={(e) => setNewRecord({ ...newRecord, periodStart: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="date"
                  label="Period End"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={newRecord.periodEnd || ''}
                  onChange={(e) => setNewRecord({ ...newRecord, periodEnd: e.target.value })}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Base Salary"
                  type="number"
                  fullWidth
                  value={newRecord.baseSalary || 0}
                  onChange={(e) => setNewRecord({ ...newRecord, baseSalary: Number(e.target.value) })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Bonuses"
                  type="number"
                  fullWidth
                  value={newRecord.bonuses || 0}
                  onChange={(e) => setNewRecord({ ...newRecord, bonuses: Number(e.target.value) })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Deductions"
                  type="number"
                  fullWidth
                  value={newRecord.deductions || 0}
                  onChange={(e) => setNewRecord({ ...newRecord, deductions: Number(e.target.value) })}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" align="right">
              Total Pay: ${calculateTotal(newRecord.baseSalary || 0, newRecord.bonuses || 0, newRecord.deductions || 0).toLocaleString()}
            </Typography>

            <TextField
              label="Notes"
              multiline
              rows={2}
              fullWidth
              value={newRecord.notes || ''}
              onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">Create Record</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollManagement;
